import { z } from 'zod';

import { RetainerFeeBreakdownType, RetainerWorkType } from '~/gql/graphql';

export const BILLING_TYPE_HOURLY = 'hourly';
export const BILLING_TYPE_FLAT = 'flat_rate';

/** Fee breakdown select: GraphQL enum values with display labels. */
export const RETAINER_FEE_BREAKDOWN_OPTIONS = [
  {
    value: RetainerFeeBreakdownType.CourtAppointed,
    label: 'Court-appointed',
  },
  {
    value: RetainerFeeBreakdownType.ProBono,
    label: 'Pro bono',
  },
  {
    value: RetainerFeeBreakdownType.PrivateRetainer,
    label: 'Private retainer',
  },
] as const satisfies ReadonlyArray<{
  value: RetainerFeeBreakdownType;
  label: string;
}>;

export type RetainerFeeBreakdownOption =
  (typeof RETAINER_FEE_BREAKDOWN_OPTIONS)[number];

export const RATE_KEY_OTHER = 'other';

export const STANDARD_RATE_KEYS = [
  'consultationRate',
  'expeditedReviewRate',
  'reportWritingRate',
  'depositionRate',
  'trialRate',
] as const;
export type StandardRateKey = (typeof STANDARD_RATE_KEYS)[number];

export type BillingType = typeof BILLING_TYPE_HOURLY | typeof BILLING_TYPE_FLAT;
export type RateKey = StandardRateKey | typeof RATE_KEY_OTHER;

/** Work type select: GraphQL `RetainerWorkType` values with display labels. */
export const WORK_TYPE_OPTIONS = [
  {
    value: RetainerWorkType.ReviewOfRecordsAndConference,
    label:
      "Review of records and conference with attorney to discuss expert's opinion",
  },
  {
    value: RetainerWorkType.GenerationOfWrittenReport,
    label: 'Generation of written report',
  },
  {
    value: RetainerWorkType.RemoteDeposition,
    label: 'Remote deposition',
  },
  {
    value: RetainerWorkType.InPersonTestimony,
    label: 'In person testimony',
  },
  {
    value: RetainerWorkType.SomethingElse,
    label: 'Something else',
  },
] as const satisfies ReadonlyArray<{
  value: RetainerWorkType;
  label: string;
}>;

const feeBreakdownFormSchema = z.union([
  z.literal(''),
  z.nativeEnum(RetainerFeeBreakdownType),
]);

const workTypeFormSchema = z
  .union([z.literal(''), z.nativeEnum(RetainerWorkType)])
  .refine((value) => value !== '', {
    message: 'Please select what this engagement will cover',
  });

function refineBillingPart(
  data: {
    billingType: string;
    hours: number;
    rateKey: string;
    customRate?: number;
    flatAmount: number;
  },
  ctx: z.RefinementCtx,
): void {
  if (data.billingType !== BILLING_TYPE_HOURLY) {
    return;
  }
  if (data.hours < 0.5) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'At least 0.5 hours',
      path: ['hours'],
    });
  }
  if (data.rateKey === RATE_KEY_OTHER) {
    const rate = data.customRate ?? 0;
    if (rate < 100) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Custom rate must be at least $100/hr',
        path: ['customRate'],
      });
    }
  }
}

export const requestRetainerSchema = z
  .object({
    title: z.string().min(1, 'Retainer title is required'),
    feeType: feeBreakdownFormSchema,
    billingType: z.enum([BILLING_TYPE_HOURLY, BILLING_TYPE_FLAT]),
    hours: z.number(),
    flatAmount: z.number(),
    workType: workTypeFormSchema,
    workTypeOtherComment: z.string().optional(),
    additionalDetails: z.string().optional(),
    rateKey: z.enum([...STANDARD_RATE_KEYS, RATE_KEY_OTHER]),
    customRate: z.number().optional(),
    customServiceLabel: z.string().optional(),
  })
  .superRefine((data, ctx) => {
    refineBillingPart(data, ctx);
    if (data.workType === RetainerWorkType.SomethingElse) {
      const comment = (data.workTypeOtherComment ?? '').trim();
      if (comment.length === 0) {
        const billingLabel =
          data.billingType === BILLING_TYPE_HOURLY
            ? 'these hours'
            : 'this engagement';
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `Please describe what ${billingLabel} will cover`,
          path: ['workTypeOtherComment'],
        });
      }
    }
  });

/** RHF / default values (empty work type until user selects). */
export type RequestRetainerFormData = z.input<typeof requestRetainerSchema>;
/** After schema validation (work type required). */
export type RequestRetainerValidatedFormData = z.output<
  typeof requestRetainerSchema
>;
